import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { ChatMention, MentionableChat } from '../shared/chatMentions';
import { MAX_CHAT_MENTIONS } from '../shared/chatMentions';
import { MessageSquare } from 'lucide-react';
import ChatMentionText from './ChatMentionText';
import { findMentionQuery, getMentionSuggestions, insertChatMention, reconcileMentionEdit, refreshChatMentionTitles, type ComposerSelection } from './chatMentionEditing';

interface Props {
  value: string;
  mentions: ChatMention[];
  onChange: (text: string, mentions: ChatMention[]) => void;
  onSend: () => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  chats: MentionableChat[];
  currentChatId?: string | null;
  onOpenChat?: (id: string) => void;
  isRecording: boolean;
  mentionRequest?: { sequence: number; caret: number };
}

const textMetrics = 'whitespace-pre-wrap break-words px-3 py-3 text-base leading-6';

export default function ChatMentionComposer({ value, mentions, onChange, onSend, textareaRef, chats, currentChatId, onOpenChat, isRecording, mentionRequest }: Props) {
  const [caret, setCaret] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
  const [dismissed, setDismissed] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const selectionRef = useRef<ComposerSelection>();
  const composingRef = useRef(false);
  const optionsRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  const pendingSelection = useRef<{ text: string; start: number; end: number }>();
  const [page, setPage] = useState<{ queryKey: string | null; limit: number }>({ queryKey: null, limit: 20 });
  const query = focused && !isRecording ? findMentionQuery(value, caret, mentions) : null;
  const queryKey = query ? `${query.start}:${query.end}:${query.search}` : null;
  const pageLimit = page.queryKey === queryKey ? page.limit : 20;
  const candidates = getMentionSuggestions(chats, query, mentions, currentChatId, pageLimit + 1);
  const suggestions = candidates.slice(0, pageLimit);
  const hasMore = candidates.length > pageLimit;
  const open = query !== null && dismissed !== queryKey;
  const selectedIndex = Math.min(activeIndex, Math.max(0, suggestions.length - 1));

  useEffect(() => {
    if (!open) setPage({ queryKey: null, limit: 20 });
  }, [open]);

  useLayoutEffect(() => {
    if (open) optionsRef.current?.querySelectorAll<HTMLElement>('[role="option"]')[selectedIndex]
      ?.scrollIntoView({ block: 'nearest' });
  }, [open, selectedIndex, queryKey]);

  useEffect(() => {
    if (composingRef.current) return;
    const input = textareaRef.current;
    const next = refreshChatMentionTitles(value, mentions, chats);
    if (next.text !== value) {
      pendingSelection.current = { text: next.text, start: next.mapOffset(input?.selectionStart ?? value.length), end: next.mapOffset(input?.selectionEnd ?? value.length) };
      setCaret(next.mapOffset(caret));
      onChange(next.text, next.mentions);
    }
  }, [value, mentions, chats, textareaRef, onChange, caret]);

  useLayoutEffect(() => {
    if (pendingSelection.current?.text !== value) return;
    textareaRef.current?.setSelectionRange(pendingSelection.current.start, pendingSelection.current.end);
    pendingSelection.current = undefined;
  }, [value, textareaRef]);

  const syncScroll = useCallback(() => {
    if (overlayRef.current && textareaRef.current) {
      overlayRef.current.scrollTop = textareaRef.current.scrollTop;
      overlayRef.current.scrollLeft = textareaRef.current.scrollLeft;
    }
  }, [textareaRef]);

  useLayoutEffect(() => {
    const input = textareaRef.current;
    if (!input) return;
    input.style.height = 'auto';
    input.style.height = `${Math.min(input.scrollHeight, 120)}px`;
    syncScroll();
  }, [value, textareaRef, syncScroll]);

  useLayoutEffect(() => {
    if (!mentionRequest) return;
    setCaret(mentionRequest.caret);
    setFocused(true);
    setDismissed(null);
    setActiveIndex(0);
    textareaRef.current?.focus({ preventScroll: true });
    textareaRef.current?.setSelectionRange(mentionRequest.caret, mentionRequest.caret);
  }, [mentionRequest, textareaRef]);

  const selectChat = (chat: MentionableChat) => {
    if (!query) return;
    const next = insertChatMention(value, mentions, query, chat);
    if (!next) return;
    onChange(next.text, next.mentions);
    setCaret(next.caret);
    setDismissed(null);
    setActiveIndex(0);
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus({ preventScroll: true });
      textareaRef.current?.setSelectionRange(next.caret, next.caret);
    });
  };

  return <div className="relative">
    {open && <div className="absolute bottom-full left-0 z-30 mb-2 max-h-[min(14.5rem,40dvh)] w-full max-w-md overflow-y-auto rounded-2xl border border-line bg-panel p-1.5 shadow-xl">
      <div ref={optionsRef} id="chat-mention-options" role="listbox" aria-label="Mencionar un chat">
      {suggestions.length ? suggestions.map((chat, index) => <button type="button" role="option"
        id={`chat-mention-option-${index}`} key={chat.id} aria-selected={index === selectedIndex} tabIndex={-1}
        onMouseDown={(event) => event.preventDefault()} onClick={() => selectChat(chat)}
        className="block min-h-11 w-full truncate rounded-xl px-3 py-2 text-left text-sm text-ink hover:bg-juris-soft"
      ><MessageSquare size={15} className="mr-2 inline-block align-text-bottom" aria-hidden="true" />{chat.title}</button>) : <p className="px-3 py-2 text-sm text-muted" role="status">
        {mentions.length >= MAX_CHAT_MENTIONS ? 'Puedes mencionar hasta 2 chats por mensaje.' : 'No hay chats que coincidan.'}
      </p>}
      </div>
      {hasMore && <button ref={moreRef} type="button" onMouseDown={event => event.preventDefault()}
        onClick={() => { setPage({ queryKey, limit: pageLimit + 20 }); textareaRef.current?.focus({ preventScroll: true }); }}
        className="min-h-11 w-full rounded-xl px-3 py-2 text-sm font-medium text-juris hover:bg-juris-soft">Ver más chats</button>}
    </div>}
    {isRecording && <p className="pointer-events-none absolute inset-x-0 top-0 px-3 py-3 text-base text-muted">Pregunta o pide a Juris</p>}
    <textarea id="chat-input" name="chat-message" inputMode="text" enterKeyHint="send"
      aria-label="Escribe tu mensaje para Juris" ref={textareaRef} value={value}
      aria-autocomplete="list" aria-haspopup="listbox" aria-controls={open ? 'chat-mention-options' : undefined}
      aria-expanded={open} aria-activedescendant={open && suggestions.length ? `chat-mention-option-${selectedIndex}` : undefined}
      onFocus={(event) => { setFocused(true); setCaret(event.currentTarget.selectionStart); }}
      onBlur={(event) => { if (!event.currentTarget.parentElement?.contains(event.relatedTarget)) setFocused(false); }}
      onBeforeInput={(event) => {
        selectionRef.current = { start: event.currentTarget.selectionStart, end: event.currentTarget.selectionEnd,
          inputType: (event.nativeEvent as InputEvent).inputType || selectionRef.current?.inputType };
      }}
      onChange={(event) => {
        const input = event.currentTarget;
        onChange(input.value, reconcileMentionEdit(value, input.value, mentions, selectionRef.current));
        selectionRef.current = undefined;
        setCaret(input.selectionStart); setActiveIndex(0); setDismissed(null);
      }}
      onSelect={(event) => {
        const input = event.currentTarget;
        setCaret(input.selectionStart === input.selectionEnd ? input.selectionStart : -1);
      }}
      onScroll={syncScroll}
      onCompositionStart={() => { composingRef.current = true; }}
      onCompositionEnd={() => { composingRef.current = false; }}
      onKeyDown={(event) => {
        if (composingRef.current || event.nativeEvent.isComposing || event.keyCode === 229) return;
        selectionRef.current = { start: event.currentTarget.selectionStart, end: event.currentTarget.selectionEnd,
          inputType: event.key === 'Backspace' ? 'deleteContentBackward' : event.key === 'Delete' ? 'deleteContentForward' : undefined };
        if (open && event.key === 'Escape') { event.preventDefault(); setDismissed(queryKey); return; }
        if (open && hasMore && event.key === 'Tab' && !event.shiftKey) { event.preventDefault(); moreRef.current?.focus(); return; }
        if (open && suggestions.length && ['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key) && !event.shiftKey) {
          event.preventDefault();
          if (event.key === 'Enter') selectChat(suggestions[selectedIndex]);
          else setActiveIndex((selectedIndex + (event.key === 'ArrowDown' ? 1 : -1) + suggestions.length) % suggestions.length);
          return;
        }
        if (event.key === 'Enter' && !event.shiftKey && window.innerWidth >= 768) { event.preventDefault(); onSend(); }
      }}
      placeholder="Pregunta o pide a Juris"
      className={`block max-h-32 min-h-12 w-full resize-none rounded-xl border-0 bg-transparent ${textMetrics} text-ink outline-none placeholder:text-muted focus:outline-none transition-opacity duration-200 [&::-webkit-scrollbar]:hidden ${isRecording ? 'invisible opacity-0' : ''}`}
      style={{ scrollbarWidth: 'none', ...(mentions.length ? { color: 'transparent', caretColor: 'rgb(var(--color-juris))' } : {}) }} rows={1} />
    {mentions.length > 0 && !isRecording && <div ref={overlayRef}
      className={`pointer-events-none absolute inset-0 overflow-hidden rounded-xl ${textMetrics} text-ink`}
      style={{ scrollbarWidth: 'none' }}>
      <ChatMentionText text={value} mentions={mentions} onOpenChat={onOpenChat} overlay />{'\u200b'}
    </div>}
  </div>;
}
